import type {Filter} from '@/modules/common/models/filters';

import {sortBy, without} from 'lodash';
import {useTranslation} from 'react-i18next';

import {
  FILTER_DESTINATION_AIRPORTS,
  FILTER_AIRLINES,
  FILTER_BEACH,
  FILTER_BOARD_TYPE,
  FILTER_CHILDREN,
  FILTER_DEPARTURE_TIME,
  FILTER_ENTERTAINMENT,
  FILTER_FACILITIES,
  FILTER_FLIGHT_TRANSFER,
  FILTER_HOTEL_CATEGORY,
  FILTER_HOTEL_RATING,
  FILTER_HOTEL_TYPE,
  FILTER_POOL,
  FILTER_PRICE,
  FILTER_PRICE_CHART,
  FILTER_PROMOTION_FLEX,
  FILTER_PROMOTION_SAFETY,
  FILTER_PROMOTION_SPECIALS,
  FILTER_RESTAURANT,
  FILTER_RETURN_TIME,
  FILTER_ROOM_ATTRIBUTES,
  FILTER_ROOM_TYPE,
  FILTER_SPORTS,
  FILTER_SUSTAINABILITY,
  FILTER_TOUR_OPERATOR,
  FILTER_WATER_SPORTS,
  FILTER_WELLNESS,
} from '@/modules/common/models/filters';
import {mapAmadeusRatingToRatingCategory} from '@/modules/common/utils/rating';
import {safeDateFormat} from '@/utils/date';

type Translations = Record<string, string>;

type Props = {
  filters: Array<Filter>;
  translations: Translations;
};

type PickFilter<T, U> = T extends {id: U} ? T : never;

export type FilterChip = {
  id: string;
  type?: string;
  label: string;
  clear: () => void;
};

const mapFilter = <Id extends string>(
  filter: PickFilter<Filter, Id>,
  getLabel: () => string
): Array<FilterChip> =>
  filter.active
    ? [
        {
          id: filter.id,
          label: getLabel(),
          clear: filter.clear,
        },
      ]
    : [];

type Ids =
  | typeof FILTER_PROMOTION_SPECIALS
  | typeof FILTER_PROMOTION_FLEX
  | typeof FILTER_PROMOTION_SAFETY
  | typeof FILTER_FLIGHT_TRANSFER
  | typeof FILTER_BOARD_TYPE
  | typeof FILTER_ROOM_TYPE
  | typeof FILTER_TOUR_OPERATOR
  | typeof FILTER_FACILITIES
  | typeof FILTER_CHILDREN
  | typeof FILTER_WELLNESS
  | typeof FILTER_POOL
  | typeof FILTER_BEACH
  | typeof FILTER_SPORTS
  | typeof FILTER_WATER_SPORTS
  | typeof FILTER_RESTAURANT
  | typeof FILTER_ENTERTAINMENT
  | typeof FILTER_HOTEL_TYPE
  | typeof FILTER_SUSTAINABILITY
  | typeof FILTER_AIRLINES
  | typeof FILTER_ROOM_ATTRIBUTES
  | typeof FILTER_DESTINATION_AIRPORTS;

const mapActiveOptions = (
  filter: PickFilter<Filter, Ids>,
  translations: Translations
) => {
  const filterValue = filter.value || [];

  return sortBy(
    filterValue.reduce<Array<FilterChip>>((chips, value) => {
      const option = filter.options.find((o) => o.value === value);
      const clear = () => {
        const nextValue = without(filterValue, value);
        filter.setValue(nextValue);
        return nextValue;
      };
      if (option) {
        chips.push({
          id: value,
          type: filter.id,
          label: option.label,
          clear,
        });
      } else if (translations?.[value] !== undefined) {
        chips.push({
          id: value,
          type: filter.id,
          label: translations[value],
          clear,
        });
      }

      return chips;
    }, []),
    (chip) => {
      const position = filter.options.findIndex(
        (option) => option.value === chip.id
      );
      return position === -1 ? filter.options.length : position;
    }
  );
};

export const useFilterChips = ({filters, translations}: Props) => {
  const {t} = useTranslation(['general', 'filters']);

  return filters.flatMap((filter) => {
    switch (filter.id) {
      case FILTER_PRICE: {
        const [valueMin, valueMax] = filter.value;

        return mapFilter<typeof FILTER_PRICE>(filter, () => {
          if (valueMin !== null && valueMax !== null) {
            return t('filters:chips.priceRange', {
              priceMin: valueMin.amount,
              priceMax: valueMax.amount,
              currency: valueMin.currency,
            });
          } else if (valueMin !== null) {
            return t('filters:chips.priceFrom', {
              price: valueMin.amount,
              currency: valueMin.currency,
            });
            // eslint-disable-next-line unicorn/no-negated-condition -- ...
          } else if (valueMax !== null) {
            return t('filters:chips.priceTo', {
              price: valueMax.amount,
              currency: valueMax.currency,
            });
          } else {
            return '';
          }
        });
      }
      case FILTER_HOTEL_CATEGORY: {
        return mapFilter<typeof FILTER_HOTEL_CATEGORY>(filter, () =>
          t('filters:category.label', {count: filter.value})
        );
      }
      case FILTER_HOTEL_RATING: {
        const option = filter.options.find(
          (optionItem) => optionItem.value === filter.value
        );
        return mapFilter<typeof FILTER_HOTEL_RATING>(filter, () =>
          t('filters:customerRating.explanationLabel.minimumAdjective', {
            // @ts-expect-error ...
            option: `$t(filters:customerRating.explanationLabel.${mapAmadeusRatingToRatingCategory(option.threshold)}, lowercase)`,
            interpolation: {skipOnVariables: false},
          })
        );
      }
      case FILTER_DEPARTURE_TIME: {
        return mapFilter<typeof FILTER_DEPARTURE_TIME>(filter, () =>
          t('filters:headlines.departureTime')
        );
      }
      case FILTER_RETURN_TIME: {
        return mapFilter<typeof FILTER_RETURN_TIME>(filter, () =>
          t('filters:headlines.returnTime')
        );
      }
      case FILTER_PRICE_CHART: {
        return mapFilter<typeof FILTER_PRICE_CHART>(filter, () =>
          t('filters:chips.departureDate', {
            date: safeDateFormat(filter.value?.startDate, 'P'),
          })
        );
      }
      case FILTER_PROMOTION_SPECIALS:
      case FILTER_PROMOTION_FLEX:
      case FILTER_PROMOTION_SAFETY:
      case FILTER_FLIGHT_TRANSFER:
      case FILTER_BOARD_TYPE:
      case FILTER_ROOM_TYPE:
      case FILTER_TOUR_OPERATOR:
      case FILTER_FACILITIES:
      case FILTER_CHILDREN:
      case FILTER_WELLNESS:
      case FILTER_POOL:
      case FILTER_BEACH:
      case FILTER_SPORTS:
      case FILTER_WATER_SPORTS:
      case FILTER_RESTAURANT:
      case FILTER_ENTERTAINMENT:
      case FILTER_HOTEL_TYPE:
      case FILTER_SUSTAINABILITY:
      case FILTER_AIRLINES:
      case FILTER_ROOM_ATTRIBUTES:
      case FILTER_DESTINATION_AIRPORTS: {
        return mapActiveOptions(filter, translations);
      }
      default: {
        return [];
      }
    }
  });
};
